import { ConvergenceHeatmap } from "../components/live/ConvergenceHeatmap";
import { KpiCard } from "../components/live/KpiCard";
import { NewsCountdown } from "../components/live/NewsCountdown";
import { PositionsCompact } from "../components/live/PositionsCompact";
import { StatusStrip } from "../components/live/StatusStrip";
import { TradeFeed } from "../components/live/TradeFeed";
import { useLivePoll } from "../hooks/useLivePoll";

function fmtCurrency(v: number | null | undefined, ccy: string): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${ccy}`;
}

function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}

export function LiveDashboardPage() {
  const snap = useLivePoll();
  const ccy = snap.account?.currency ?? "USD";

  // Today's P/L as a % of the starting balance. The Risk service tracks
  // realised P/L; for live equity-vs-balance we use current equity - balance.
  // Falling back to the risk service's realised PnL keeps the number
  // meaningful even when the account endpoint is degraded.
  const equity = snap.account?.equity ?? null;
  const balance = snap.account?.balance ?? null;
  const unrealised =
    equity != null && balance != null ? equity - balance : null;
  const realised = snap.risk?.today?.realised_pnl ?? 0;
  const todayPnl = (unrealised ?? 0) + realised;
  const startBalance = snap.risk?.today?.starting_balance ?? balance ?? null;
  const todayPnlPct =
    startBalance != null && startBalance > 0
      ? (todayPnl / startBalance) * 100
      : null;

  const dailyLimitPct = snap.risk?.limits.daily_loss_limit_pct ?? null;
  const maxPositions = snap.risk?.limits.max_open_positions_total ?? 0;

  const pnlTone =
    todayPnl > 0 ? "success" : todayPnl < 0 ? "danger" : "default";

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Live</h1>
          <p className="text-xs text-fg-muted">
            big-screen view · auto-refresh 2s
          </p>
        </div>
        <span className="font-mono text-xs text-fg-muted">
          {snap.lastTick ? `updated ${snap.lastTick.toLocaleTimeString()}` : "loading…"}
        </span>
      </header>

      {/* Row 1: hero KPIs. 4-up on wide screens; collapses to 2x2 on
          laptops and 1-col on tablets. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Balance"
          value={fmtCurrency(snap.account?.balance, ccy)}
          hint={snap.account ? `login ${snap.account.login}` : undefined}
        />
        <KpiCard
          label="Equity"
          value={fmtCurrency(snap.account?.equity, ccy)}
          hint={
            snap.account
              ? `margin free ${snap.account.margin_free.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
              : undefined
          }
        />
        <KpiCard
          label="P/L today"
          value={
            startBalance == null
              ? "—"
              : `${todayPnl >= 0 ? "+" : ""}${todayPnl.toFixed(2)}`
          }
          hint={
            todayPnlPct != null && dailyLimitPct != null
              ? `${fmtPct(todayPnlPct)}  ·  limit ${dailyLimitPct}%`
              : undefined
          }
          tone={pnlTone}
        />
        <KpiCard
          label="Open positions"
          value={`${snap.positions.length}${maxPositions ? ` / ${maxPositions}` : ""}`}
          hint={
            snap.bridge?.connected
              ? `bridge ${snap.bridge.mt5_login ?? ""}`
              : "bridge offline"
          }
          tone={snap.bridge?.connected ? "default" : "danger"}
        />
      </div>

      {/* Row 2: status strip. */}
      <StatusStrip
        bridge={snap.bridge}
        risk={snap.risk}
        matrix={snap.matrix}
        positionsCount={snap.positions.length}
      />

      {/* Row 3: 2x2 main grid. Lock each cell at a comfortable min-height
          so the dashboard doesn't collapse to a tiny strip when data is
          empty — important for the "at a glance from across the room"
          ergonomics this page exists for. */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="min-h-[18rem]">
          <PositionsCompact
            positions={snap.positions}
            bridgeConnected={!!snap.bridge?.connected}
          />
        </div>
        <div className="min-h-[18rem]">
          <TradeFeed trades={snap.trades} />
        </div>
        <div className="min-h-[18rem]">
          <NewsCountdown events={snap.news} />
        </div>
        <div className="min-h-[18rem]">
          <ConvergenceHeatmap matrix={snap.matrix} />
        </div>
      </div>
    </div>
  );
}
